'use client'
import React, {
  FunctionComponent,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react'
import cn from 'classnames'
import { AnimatePresence, motion } from 'framer-motion'

import { MktuClassWord } from '@/services/Api/generated'
import { Input } from '@/components/Input'
import { Icon } from '@/icons'
import { Word } from '@/shared/Mktu/components'

import styles from './MktuClassWordList.module.scss'
import { observer } from 'mobx-react-lite'

// Получаем размер скрытой части последнего элемента
function getHiddenPartElement(container: HTMLDivElement) {
  const elements = Array.from(container.children)
  const containerRect = container.getBoundingClientRect()

  const edgeElement = elements.findLast((element) => {
    const elementRect = element.getBoundingClientRect()
    // Элемент считается видимым, если он хотя бы частично в контейнере
    const isVisible =
      elementRect.bottom > containerRect.top &&
      elementRect.top < containerRect.bottom
    return isVisible
  })

  if (edgeElement == null) {
    return 0
  }

  const edgeElementRect = edgeElement.getBoundingClientRect()
  return Math.max(edgeElementRect.bottom - containerRect.bottom, 0)
}

enum ListView {
  LIST = 'LIST',
  GRID = 'GRID',
}

const COLLAPSED_SELECTED_WORDS_LIMIT = 10

type MktuClassWordListProps = {
  className?: string
  words: MktuClassWord[]
  onlyNice?: boolean
  selectedWords: MktuClassWord[]
  onSelect: (word: MktuClassWord) => void
  onSelectAll: () => void
  onResetAll: () => void
}
export const MktuCLassWordList: FunctionComponent<MktuClassWordListProps> =
  observer(
    ({
      className,
      words,
      onlyNice,
      selectedWords,
      onSelect,
      onSelectAll,
      onResetAll,
    }) => {
      const listRef = useRef<HTMLDivElement>(null)
      const [wordsForSearch, setWordsForSearch] = useState('')
      const [typeView, setTypeView] = useState(ListView.GRID)
      const [expandedSelectedWords, setExpandedSelectedWords] = useState(false)

      const visibleSelectedWords = useMemo(() => {
        return expandedSelectedWords
          ? selectedWords
          : selectedWords.slice(0, COLLAPSED_SELECTED_WORDS_LIMIT)
      }, [expandedSelectedWords, selectedWords])

      const toggleSelectedWords = useCallback(() => {
        setExpandedSelectedWords((isExpanded) => !isExpanded)
      }, [])

      const filterWords = useMemo(() => {
        let result = words.filter((w) =>
          w.word?.toLowerCase().includes(wordsForSearch.toLowerCase())
        )

        if (onlyNice) {
          result = result.filter((w) => w.isNice)
        }

        return result
      }, [wordsForSearch, words, onlyNice])

      const isSelectedAll = useMemo(() => {
        const visibleWords = onlyNice ? words.filter((w) => w.isNice) : words

        const unselectedWords = visibleWords.filter(
          (w) =>
            selectedWords.find(
              (selectedWord) => selectedWord.uuid === w.uuid
            ) == null
        )

        return unselectedWords.length === 0
      }, [onlyNice, selectedWords, words])

      const setGridView = useCallback(() => {
        setTypeView(ListView.GRID)
      }, [])

      const setListView = useCallback(() => {
        setTypeView(ListView.LIST)
      }, [])

      const selectAll = useCallback(() => {
        if (isSelectedAll) {
          onResetAll()
        } else {
          onSelectAll()
        }
      }, [isSelectedAll, onResetAll, onSelectAll])

      const scrollUp = useCallback(() => {
        if (listRef.current != null) {
          const listEl = listRef.current
          const additionalScrollSize = getHiddenPartElement(listEl)

          if (additionalScrollSize > 1) {
            listEl.scrollBy(0, -28 + additionalScrollSize)
          } else {
            listEl.scrollBy(0, -36)
          }
        }
      }, [])

      const scrollDown = useCallback(() => {
        if (listRef.current != null) {
          const listEl = listRef.current
          const additionalScrollSize = getHiddenPartElement(listEl)
          if (additionalScrollSize > 1) {
            listEl.scrollBy(0, additionalScrollSize)
          } else {
            listEl.scrollBy(0, 36 + additionalScrollSize)
          }
        }
      }, [])

      return (
        <div className={cn(className, styles.container)}>
          <div className={styles.search}>
            <div className={styles.searchInputContainer}>
              <Icon name="searchSimple" className={styles.searchIcon} />
              <Input
                placeholder="Например: одежда"
                className={styles.searchInput}
                value={wordsForSearch}
                onChange={setWordsForSearch}
              />
            </div>
          </div>
          <div className={styles.settings}>
            <button
              type="button"
              className={styles.selectAll}
              onClick={selectAll}
            >
              <span className={styles.selectAllMark}>
                {isSelectedAll ? (
                  <Icon name="check" className={styles.markIcon} />
                ) : null}
              </span>
              Выбрать все
            </button>
            <div className={styles.settingsRight}>
              <button
                type="button"
                onClick={setGridView}
                className={styles.buttonView}
              >
                <Icon
                  name="gridView"
                  className={cn(styles.buttonViewIcon, {
                    [styles.isActive]: typeView === ListView.GRID,
                  })}
                />
              </button>
              <button
                type="button"
                onClick={setListView}
                className={styles.buttonView}
              >
                <Icon
                  name="listView"
                  className={cn(styles.buttonViewIcon, {
                    [styles.isActive]: typeView === ListView.LIST,
                  })}
                />
              </button>
            </div>
          </div>
          <AnimatePresence initial={false}>
            {selectedWords.length > 0 ? (
              <motion.div
                layout="size"
                className={styles.selectedWordsCollapse}
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                <div className={styles.selectedWordsList}>
                  {visibleSelectedWords.map((word) => (
                    <Word
                      key={word.uuid}
                      id={word}
                      name={word.word}
                      onClick={onSelect}
                      className={styles.selectedWord}
                      isNice={word.isNice}
                      isDeletable
                      isSelected
                      maxWidth={expandedSelectedWords ? undefined : 240}
                    />
                  ))}

                  {selectedWords.length > COLLAPSED_SELECTED_WORDS_LIMIT ? (
                    <button
                      type="button"
                      className={styles.selectedWordsExpandButton}
                      onClick={toggleSelectedWords}
                    >
                      {expandedSelectedWords
                        ? 'Свернуть'
                        : `+${
                            selectedWords.length -
                            COLLAPSED_SELECTED_WORDS_LIMIT
                          }`}
                    </button>
                  ) : null}
                </div>
              </motion.div>
            ) : null}
          </AnimatePresence>
          <div className={styles.listWrap}>
            <div
              ref={listRef}
              className={cn(styles.list, {
                [styles.isColumn]: typeView === ListView.LIST,
              })}
            >
              {filterWords.map((word) => (
                <button
                  key={word.uuid}
                  type="button"
                  onClick={() => onSelect(word)}
                  className={cn(styles.item, {
                    [styles.isActive]:
                      selectedWords.find((w) => w.word === word.word) != null,
                  })}
                >
                  {word.word}
                  {word.isNice ? (
                    <span className={styles.charNice}>N</span>
                  ) : null}
                </button>
              ))}
            </div>
            <div className={styles.listControl}>
              <button
                type="button"
                className={styles.listControlButton}
                onClick={scrollUp}
              >
                <Icon name="arrow" className={styles.listControlIconTop} />
              </button>
              <button
                type="button"
                className={styles.listControlButton}
                onClick={scrollDown}
              >
                <Icon name="arrow" className={styles.listControlIconBottom} />
              </button>
            </div>
          </div>
        </div>
      )
    }
  )
